import { useState } from "react";

import { AccountTokenType } from "@eisox/api/core-api";
import { toast, Field, PhoneInput, UnprotectedRouteResponsiveCard } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { CheckedIcon } from "@eisox/icons";
import type { z } from "@eisox/zod";
import { useSelector } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { accountApi } from "~/api";
import {
  assertAccountTokenUsable,
  enrollAccountSchema,
  PhoneNumberValidationDialog,
  usePasswordConditions,
  type EnrollAccountSchemaType,
} from "~/features/account";
import { m } from "~/paraglide/messages";

export const Route = createFileRoute("/enroll-account/$token")({
  beforeLoad: ({ context: { queryClient }, params: { token } }) =>
    assertAccountTokenUsable(queryClient, token, AccountTokenType.ACCOUNT),
  component: RouteComponent,
});

function RouteComponent() {
  const { token } = Route.useParams();
  const { auth, redirectUrl } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const { validatePassword, passwordErrors } = usePasswordConditions();
  const [phoneValidationOpen, setPhoneValidationOpen] = useState(false);

  const { mutateAsync: enroll } = useMutation({
    meta: { toast: { success: m.enroll_account_success() } },
    mutationFn: (value: EnrollAccountSchemaType) =>
      accountApi.activate(token, {
        firstName: value.firstName,
        lastName: value.lastName,
        company: value.company,
        phoneNumber: value.phoneNumber || undefined,
        password: value.password,
      }),
    onSuccess: async ({ token: sessionToken, user }, { phoneNumber }) => {
      if (!sessionToken) {
        if (redirectUrl) window.location.assign(redirectUrl);
        else await navigate({ to: "/sign-in", search: { redirect: "" } });
        return;
      }
      await auth.adoptSession({ token: sessionToken, user });
      // Activation already texted the first code, so the dialog only has to collect it.
      if (phoneNumber) {
        toast.success(m.code_send());
        setPhoneValidationOpen(true);
        return;
      }
      await navigate({ to: "/" });
    },
  });

  const form = useAppForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      company: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
    },
    validators: {
      onSubmit: enrollAccountSchema,
    },
    onSubmit: ({ value }) => enroll(value),
  });

  const passwordErrorMessage = useSelector(
    form.store,
    state => (state.fieldMeta.password?.errors as z.core.$ZodIssue[] | undefined)?.[0]?.message,
  );

  return (
    <UnprotectedRouteResponsiveCard version={import.meta.env.VITE_VERSION}>
      <form
        className="gap-10 flex flex-col items-center"
        data-testid="EnrollAccount"
        onSubmit={e => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <div className="gap-10 grid w-full grid-cols-2 [grid-template-areas:'firstName_password'_'lastName_confirmPassword'_'company_passwordRules'_'phoneNumber_passwordRules'] max-[640px]:[grid-template-areas:'firstName_firstName'_'lastName_lastName'_'company_company'_'phoneNumber_phoneNumber'_'password_password'_'confirmPassword_confirmPassword'_'passwordRules_passwordRules']">
            <form.AppField name="firstName">
              {field => (
                <field.TextField
                  classNames={{ root: "w-full grid-area:firstName", control: "w-full" }}
                  label={m.first_name()}
                  placeholder={m.first_name_placeholder()}
                />
              )}
            </form.AppField>
            <form.AppField name="password">
              {field => (
                <field.PasswordField
                  classNames={{ root: "w-full grid-area:password", control: "w-full" }}
                  label={m.password()}
                  placeholder={m.password_placeholder()}
                  onChange={e => validatePassword(e.target.value)}
                />
              )}
            </form.AppField>
            <form.AppField name="lastName">
              {field => (
                <field.TextField
                  classNames={{ root: "w-full grid-area:lastName", control: "w-full" }}
                  label={m.last_name()}
                  placeholder={m.last_name_placeholder()}
                />
              )}
            </form.AppField>
            <form.AppField name="confirmPassword">
              {field => (
                <field.PasswordField
                  classNames={{ root: "w-full grid-area:confirmPassword", control: "w-full" }}
                  label={m.confirm_password()}
                  placeholder={m.confirm_password_placeholder()}
                />
              )}
            </form.AppField>
            <form.AppField name="company">
              {field => (
                <field.TextField
                  classNames={{ root: "w-full grid-area:company", control: "w-full" }}
                  label={m.company()}
                  placeholder={m.company_placeholder()}
                />
              )}
            </form.AppField>
            <form.Field name="phoneNumber">
              {field => (
                <Field.Root className="w-full [grid-area:phoneNumber]">
                  <Field.Label>{m.phone()}</Field.Label>
                  <PhoneInput.Input
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={field.handleChange}
                    countrySelectProps={{ disabled: true }}
                    defaultCountry="FR"
                  />
                  {(field.state.meta.errors as unknown as z.core.$ZodIssue[] | undefined)?.map(error => (
                    <Field.Error key={error.message} match>
                      {error.message}
                    </Field.Error>
                  ))}
                </Field.Root>
              )}
            </form.Field>
            <div className="gap-2.5 [&_div]:gap-2.5 [&_svg]:h-4 [&_svg]:w-4 flex flex-col [grid-area:passwordRules] [&_div]:flex [&_div]:items-center [&_div]:text-base [&_svg]:shrink-0 [&_svg]:text-primary-darker-gray [&_svg]:aria-checked:text-primary-green [&_svg]:aria-invalid:text-primary-red">
              {Object.entries(passwordErrors).map(([key, { isValid, translation }]) => (
                <div key={key}>
                  <CheckedIcon aria-checked={isValid} aria-invalid={passwordErrorMessage === key} />
                  <p className="whitespace-pre-line">{translation}</p>
                </div>
              ))}
            </div>
          </div>
          <form.SubmitButton label={m.enroll_account()} />
        </form.AppForm>
      </form>

      {phoneValidationOpen && (
        <PhoneNumberValidationDialog
          open
          // Skipping verification still leaves the account usable — the session is already live.
          onOpenChange={(open: boolean) => {
            if (!open) void navigate({ to: "/" });
          }}
          onSuccess={() => {
            void navigate({ to: "/" });
          }}
        />
      )}
    </UnprotectedRouteResponsiveCard>
  );
}
